import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LayoutDashboard, LogOut, Bell, Receipt, UserCircle } from "lucide-react";
import { useBT } from "@/i18n/billingText";
import { useAuth } from "@/context/AuthContext";
import { LanguageSelector } from "@/components/common/LanguageSelector";
import { getAccountUnread } from "@/lib/portalApi";

export const AccountLayout = () => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const { bt } = useBT();

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const d = await getAccountUnread();
        if (alive) setUnread(d?.total || 0);
      } catch (e) { /* ignore */ }
    };
    poll();
    const iv = setInterval(poll, 20000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/account/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background text-slate-100 flex flex-col font-sans">
      <header className="h-16 border-b border-white/10 glass flex items-center justify-between px-4 md:px-8">
        <NavLink to="/account" className="flex items-center gap-3" data-testid="account-brand">
          <img src="/venka-logo.png" alt="VENKA World Express" className="h-11 w-auto" />
          <span className="hidden sm:block leading-tight">
            <span className="block text-[10px] uppercase tracking-[0.18em] text-venka-green-light">
              {t("account.login.title")}
            </span>
          </span>
        </NavLink>
        <div className="flex items-center gap-3">
          <NavLink
            to="/account"
            end
            className={({ isActive }) =>
              `hidden sm:flex items-center gap-2 text-sm px-3 py-1.5 rounded-full transition-colors ${
                isActive ? "bg-venka-blue/20 text-venka-green-light" : "text-slate-300 hover:bg-white/5"
              }`
            }
          >
            <LayoutDashboard className="h-4 w-4" /> {t("account.nav.dashboard")}
          </NavLink>
          <NavLink
            to="/account/billing"
            className={({ isActive }) =>
              `flex items-center gap-2 text-sm px-3 py-1.5 rounded-full transition-colors ${
                isActive ? "bg-venka-blue/20 text-venka-green-light" : "text-slate-300 hover:bg-white/5"
              }`
            }
            data-testid="account-nav-billing"
          >
            <Receipt className="h-4 w-4" /> <span className="hidden sm:inline">{bt("myBilling")}</span>
          </NavLink>
          <span className="relative" data-testid="account-messages-indicator">
            <Bell className="h-5 w-5 text-slate-300" />
            {unread > 0 && (
              <span className="absolute -top-1.5 -right-1.5 rounded-full bg-venka-green px-1 min-w-[16px] text-center text-[9px] font-bold text-white" data-testid="account-unread-badge">{unread}</span>
            )}
          </span>
          <LanguageSelector />
          <NavLink to="/account/profile" data-testid="account-nav-profile" title={{ pt: "O meu perfil", fr: "Mon profil" }[(i18n.language || "").slice(0, 2)] || "My profile"}
            className={({ isActive }) => `rounded-full ${isActive ? "ring-2 ring-venka-green-light" : ""}`}>
            {user?.avatar_url ? <img src={user.avatar_url} alt="" className="h-8 w-8 rounded-full object-cover" />
              : <UserCircle className="h-7 w-7 text-slate-300 hover:text-white" />}
          </NavLink>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-red-300"
            data-testid="account-logout"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">{t("account.nav.logout")}</span>
          </button>
        </div>
      </header>
      <main className="flex-1">
        <div className="mx-auto max-w-5xl px-4 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AccountLayout;
